import styled from "@emotion/styled";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { Button, IconButton, Typography, CircularProgress, Box, useTheme, useMediaQuery } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import React, { useEffect, useState, useRef } from "react";
import { useOutletContext } from "react-router-dom";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  postWithQueryApiServices,
  putWithQueryApiServices,
} from "../../../apis/api";
import { apiRoutes } from "../../../apis/apiPath";
import { categoryFormInitVal } from "../../../constants";
import { StyledTypography } from "../../../styles/commonStyles";
import { IconColor } from "../../../styles/constants";
import { errorToast, successToast } from "../../../utils/helper";
import { useTenantGuard } from "../../../hooks/useTenantGuard";

const StyledInput = styled("input")(({ theme, disabled }) => ({
  backgroundColor: disabled ? "#f5f5f5" : "white",
  borderRadius: 4,
  borderWidth: 1,
  borderStyle: "solid",
  borderColor: disabled ? "#e0e0e0" : "#bdbdbd",
  height: 26,
  margin: "0 4px 0 12px",
  [theme.breakpoints.down("sm")]: {
    margin: "0 2px",
    paddingLeft: 6,
    paddingRight: 6,
    minWidth: 80,
  },
  cursor: disabled ? "not-allowed" : "text",
  flex: 1,
  minWidth: 100,
  paddingLeft: 8,
  paddingRight: 8,
}));

const CriteriaOptBox = styled("div")(({ theme, isDragging }) => ({
  display: "flex",
  alignItems: "center",
  backgroundColor: isDragging ? "#b3d9f2" : "#deedf5",
  boxSizing: "border-box",
  padding: "10px 12px",
  margin: "12px 0",
  gap: 8,
  [theme.breakpoints.down("sm")]: {
    padding: "8px 10px",
    margin: "8px 0",
    gap: 6,
  },
  borderRadius: 20,
  transition: "background-color 0.2s ease",
  cursor: "grab",
  "&:active": {
    cursor: "grabbing",
  },
}));

const FormContainer = styled("div")(({ theme, disabled }) => ({
  maxHeight: "80vh",
  height: "80vh",
  [theme.breakpoints.down("lg")]: {
    height: "auto",
    maxHeight: "none",
  },
  overflow: "hidden",
  position: "relative",
  filter: disabled ? "blur(5px)" : "none",
  transition: "filter 0.3s ease",
  "&::after": disabled ? {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.05)",
    cursor: "not-allowed",
    zIndex: 10,
    borderRadius: 10,
  } : {},
}));

const InnerContainer = styled("div")(({ theme, disabled, isActive }) => ({
  height: "100%",
  [theme.breakpoints.down("lg")]: {
    height: "auto",
    paddingBottom: "20px",
  },
  display: "flex",
  flexDirection: "column",
  borderRadius: 10,
  boxSizing: "border-box",
  position: "relative",
  filter: !isActive || disabled ? "blur(5px)" : "none",
  transition: "filter 0.3s ease",
  backgroundColor: disabled ? "#f5f5f5" : (isActive ? "white" : "#f5f5f5"),
  opacity: disabled ? 0.7 : (isActive ? 1 : 0.6),
  pointerEvents: disabled ? "none" : (isActive ? "auto" : "none"),
  cursor: disabled ? "not-allowed" : (isActive ? "auto" : "not-allowed"),
}));

const StyledSelect = styled("select")(({ theme, disabled, typeval }) => ({
  backgroundColor: disabled ? "#f5f5f5" : 
    (typeval === "number" ? "#e3f2fd" : 
     typeval === "date" ? "#e8f5e9" : "#f3e5f5"),
  color: 
    typeval === "number" ? "#1565c0" : 
    typeval === "date" ? "#2e7d32" : "#7b1fa2",
  borderRadius: 4,
  borderWidth: 1,
  borderStyle: "solid",
  borderColor: disabled ? "#e0e0e0" : "#bdbdbd",
  height: 26,
  margin: "0 4px 0 12px",
  [theme.breakpoints.down("sm")]: {
    margin: "0 2px",
  },
  cursor: disabled ? "not-allowed" : "pointer",
  paddingLeft: 4,
  paddingRight: 4,
  fontSize: 11,
  fontWeight: 600,
  "&:focus": {
    outline: "none",
    borderColor: 
      typeval === "number" ? "#1565c0" : 
      typeval === "date" ? "#2e7d32" : "#7b1fa2",
  },
}));

// const OrderBadge = styled("div")({
//   minWidth: 24,
//   height: 24,
//   borderRadius: "50%",
//   backgroundColor: "#1976d2",
//   color: "white",
//   display: "flex",
//   alignItems: "center",
//   justifyContent: "center",
//   fontSize: 12,
//   fontWeight: 600,
// });

// Auto-detect type based on value
const detectType = (value) => {
  if (value === "" || value === null || value === undefined) {
    return "text";
  }
  const trimmedValue = value.toString().trim();
  if (trimmedValue === "") {
    return "text";
  }
  const numberPattern = /^-?\d+(\.\d+)?$/;
  if (numberPattern.test(trimmedValue)) {
    return "number";
  }
  return "text";
};

// Sortable Criteria Item Component
const SortableCriteriaItem = ({ 
  item, 
  index, 
  onValueChange, 
  onTypeChange,
  onRemove, 
  disabled 
}) => {
  const isDocDate = item?.value === "Document Date";
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id, disabled: disabled || isDocDate });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <CriteriaOptBox isDragging={isDragging}>
        <div 
          {...attributes} 
          {...listeners} 
          style={{ 
            display: "flex", 
            cursor: (disabled || isDocDate) ? "not-allowed" : "grab",
            touchAction: "none"
          }}
        >
          <DragIndicatorIcon style={{ color: (disabled || isDocDate) ? "#ccc" : "#666" }} />
        </div>
        
        {/* <OrderBadge>{index + 1}</OrderBadge> */}
        
        <StyledSelect
          value={item?.type || "text"}
          typeval={item?.type}
          onChange={(e) => onTypeChange(e.target.value, index)}
          disabled={disabled || isDocDate}
        >
          <option value="text">Text</option>
          <option value="number">Number</option>
          <option value="date">Date</option>
        </StyledSelect>
        
        <StyledInput
          name="value"
          type="text"
          onChange={(e) => onValueChange(e.target.value, index)}
          value={item?.value}
          disabled={disabled || isDocDate}
          placeholder="Enter criteria value"
        />
        
        <IconButton
          onClick={() => onRemove(index)}
          disabled={disabled || isDocDate}
          style={{ cursor: (disabled || isDocDate) ? "not-allowed" : "pointer" }}
        >
          <DeleteOutlineIcon
            style={{
              color: (disabled || isDocDate) ? "#ccc" : IconColor,
              fontSize: 26,
            }}
          />
        </IconButton>
      </CriteriaOptBox>
    </div>
  );
};

export const CategoryForm = ({
  categoryListRefetch,
  companyListRefetch,
  companyForm,
  setCompanyForm,
  categoryData,
  setCategoryData,
  addNewCategory,
  setAddNewCategory,
  setSelectedCategories,
  hasUnsavedChanges,
  setHasUnsavedChanges,
  isEditingCenterPane,
  setIsEditingCenterPane,
  isCenterPaneDisabled,
  onFormComplete,
}) => {
  const [categoryValues, setCategoryValues] = useState(categoryFormInitVal);
  const [criteriaValues, setCriteriaValues] = useState([]);
  const originalCriteriaRef = useRef([]);
  const hasUnsavedChangesRef = useRef(false);
  const loadedCategoryRef = useRef(null);
  hasUnsavedChangesRef.current = hasUnsavedChanges;
  const [nextId, setNextId] = useState(1);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { currentCompany, currentTenant, tenantString } = useOutletContext();
  const { isTenantBlocked, tenantMessage } = useTenantGuard(currentTenant);

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const checkForChanges = (newCriteriaValues) => {
    const original = originalCriteriaRef.current;
    
    if (newCriteriaValues.length !== original.length) {
      return true;
    }
    
    for (let i = 0; i < newCriteriaValues.length; i++) {
      if (
        newCriteriaValues[i].type !== original[i]?.type ||
        newCriteriaValues[i].value !== original[i]?.value ||
        newCriteriaValues[i].orderby !== original[i]?.orderby
      ) {
        return true;
      }
    }
    
    return false;
  };

  // const formHandleChange = (name, value) => {
  //   setCategoryValues((prev) => ({
  //     ...prev,
  //     [name]: value,
  //   }));
  // };

  const addCriteria = () => {
    if (isTenantBlocked || isCenterPaneDisabled) {
      if (isTenantBlocked) errorToast(tenantMessage);
      return;
    }
    const newCriteriaValues = [...criteriaValues, { 
      id: `criteria-${nextId}`, 
      type: "text", 
      value: "",
      orderby: criteriaValues.length + 1 
    }];
    setNextId(nextId + 1);
    setCriteriaValues(newCriteriaValues);
    
    setIsEditingCenterPane(true);
    setHasUnsavedChanges(checkForChanges(newCriteriaValues));
  };

  const removeCriteria = (index) => {
    if (isTenantBlocked || isCenterPaneDisabled) {
      if (isTenantBlocked) errorToast(tenantMessage);
      return;
    }

    if (criteriaValues[index]?.value === "Document Date") {
      return errorToast("Document Date criteria cannot be removed");
    }

    let splicedValue = [...criteriaValues];
    splicedValue.splice(index, 1);
    
    // Re-index orderby after removal
    splicedValue = splicedValue.map((item, idx) => ({
      ...item,
      orderby: idx + 1
    }));
    
    setCriteriaValues(splicedValue);
    setHasUnsavedChanges(checkForChanges(splicedValue));
  };

  const handleCriteriaValueChange = (value, index) => {
    let copyValue = [...criteriaValues];
    copyValue[index] = { 
      ...copyValue[index], 
      value: value,
    };
    setCriteriaValues(copyValue);
    
    setIsEditingCenterPane(true);
    setHasUnsavedChanges(checkForChanges(copyValue));
  };

  const handleCriteriaTypeChange = (type, index) => {
    let copyValue = [...criteriaValues];
    copyValue[index] = { 
      ...copyValue[index], 
      type: type,
    };
    setCriteriaValues(copyValue);
    
    setIsEditingCenterPane(true);
    setHasUnsavedChanges(checkForChanges(copyValue));
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setCriteriaValues((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        let newIndex = items.findIndex((item) => item.id === over.id);
        if (oldIndex < 0 || newIndex < 0) return items;

        if (items[oldIndex]?.value === "Document Date") return items;

        const documentDateLocked = items[0]?.value === "Document Date";
        if (documentDateLocked && newIndex === 0) newIndex = 1;
        if (oldIndex === newIndex) return items;

        const newItems = arrayMove(items, oldIndex, newIndex);
        const reindexedItems = newItems.map((item, idx) => ({
          ...item,
          orderby: idx + 1
        }));

        setIsEditingCenterPane(true);
        setHasUnsavedChanges(checkForChanges(reindexedItems));

        return reindexedItems;
      });
    }
  };

  const sumbitCompanyForm = () => {
    if (isTenantBlocked) {
      errorToast(tenantMessage);
      return;
    }
    
    const fieldCheckCrioteria = criteriaValues?.every(
      (val) => val.value !== ""
    );
    if (!fieldCheckCrioteria) {
      return errorToast("Please fill all criteria values");
    }

    if (categoryData?.categoryID) {
      updateCategory.mutate();
    } else {
      createCategory.mutate();
    }
  };

  const handleCancel = () => {
    // Reset to original values if editing existing category
    if (categoryData?.categoryID && originalCriteriaRef.current.length > 0) {
      setCriteriaValues(JSON.parse(JSON.stringify(originalCriteriaRef.current)));
      setHasUnsavedChanges(false);
      setIsEditingCenterPane(false);
    } else {
      // Clear everything if new category
      setCategoryValues(categoryFormInitVal);
      setCriteriaValues([]);
      originalCriteriaRef.current = [];
      
      if (onFormComplete) {
        onFormComplete();
      }
    }
  };

  const createCategory = useMutation({
    mutationKey: ["createCategoryform"],
    mutationFn: () =>
      postWithQueryApiServices(
        apiRoutes.CREATE_CATEGORY,
        `selectedCompany=${currentCompany}${tenantString}`,
        categoryValues
      ),
    onSuccess: ({ data }) => {
      if (criteriaValues?.length > 0 && data?.insertId) {
        let workData = {
          criteriaList: criteriaValues?.map((item, index) => ({
            type: item?.type,
            criteria: item?.value,
            orderby: index + 1  // Send the order
          })),
          categoryID: data?.insertId,
        };
        updateCriteriaByCategory.mutate(workData);
      } else {
        categoryListRefetch();
        successToast("Category added Successfully");
        setCategoryValues(categoryFormInitVal);
        if (onFormComplete) onFormComplete();
      }
    },
    onError: (error) => {
      if (error?.response?.data?.code !== 'TENANT_NOT_ACTIVE') {
        errorToast(error?.response?.data?.message || "Failed to create category");
      }
    }
  });

  const updateCategory = useMutation({
    mutationKey: ["updateCategoryform"],
    mutationFn: () =>
      putWithQueryApiServices(
        apiRoutes.UPDATE_CATEGORY,
        `selectedCompany=${currentCompany}${tenantString}`,
        categoryValues
      ),
    onSuccess: () => {
      if (categoryData?.categoryID) {
        let workData = {
          criteriaList: criteriaValues?.map((item, index) => ({
            type: item?.type,
            criteria: item?.value,
            orderby: index + 1  // Send the order
          })),
          categoryID: categoryData?.categoryID,
        };
        updateCriteriaByCategory.mutate(workData);
      } else {
        categoryListRefetch();
        successToast("Category Updated Successfully");
        setCategoryValues(categoryFormInitVal);
        if (onFormComplete) onFormComplete();
      }
    },
    onError: (error) => {
      if (error?.response?.data?.code !== 'TENANT_NOT_ACTIVE') {
        errorToast(error?.response?.data?.message || "Failed to update category");
      }
    }
  });

  const updateCriteriaByCategory = useMutation({
    mutationKey: ["updateCriteriaByCatId"],
    mutationFn: (workData) =>
      putWithQueryApiServices(
        apiRoutes.UPDATE_CATEGORY_CRITERIA,
        `selectedCompany=${currentCompany}${tenantString}`,
        workData
      ),
    onSuccess: async () => {
      // Refetch to get updated data
      await categoryListRefetch();
      
      successToast(
        categoryData?.categoryID
          ? "Category Updated Successfully"
          : "Category added Successfully"
      );
      
      // Update the original reference to the new saved state
      const updatedCriteriaList = criteriaValues?.map((item, idx) => ({
        id: item.id,
        type: item?.type,
        value: item?.value,
        orderby: idx + 1
      }));
      originalCriteriaRef.current = JSON.parse(JSON.stringify(updatedCriteriaList));
      
      setHasUnsavedChanges(false);
      setIsEditingCenterPane(false);
      
      // Keep category selected - don't call onFormComplete
    },
  });

  useEffect(() => {
    const categoryKey = categoryData?.categoryID || (addNewCategory ? "new" : "none");
    const sameCategory = loadedCategoryRef.current === categoryKey;
    if (sameCategory && hasUnsavedChangesRef.current) return;
    loadedCategoryRef.current = categoryKey;

    if (categoryData?.categoryID) {
      setCategoryValues({
        name: categoryData?.name,
        expiration: categoryData?.expiration,
        categoryID: categoryData?.categoryID,
      });

      if (categoryData?.criteriaList?.length > 0) {
        let criteriaList = categoryData?.criteriaList?.map((item, idx) => ({
          id: `criteria-${idx}`,
          type: typeof item === 'object' ? (item.datatype || detectType(item.criteria)) : detectType(item),
          value: typeof item === 'object' ? item.criteria : item,
          orderby: idx + 1
        }));

        // Sort Document Date to the top if it exists
        const docDateIndex = criteriaList.findIndex(c => c.value === "Document Date");
        if (docDateIndex > 0) {
            const [docDate] = criteriaList.splice(docDateIndex, 1);
            criteriaList.unshift(docDate);
        }

        setCriteriaValues(criteriaList);
        originalCriteriaRef.current = JSON.parse(JSON.stringify(criteriaList));
        setNextId(criteriaList.length);
      } else {
        // If existing category has NO criteria at all, we still don't force it 
        // until the migration runs or the user adds it.
        setCriteriaValues([]);
        originalCriteriaRef.current = [];
        setNextId(1);
      }
      
      setHasUnsavedChanges(false);
    } else if (addNewCategory) {
      // For NEW categories, we DO want "Document Date" as the default starting criteria
      setCategoryValues(categoryFormInitVal);
      const defaultCriteria = [{
          id: `criteria-doc-date`,
          type: "date",
          value: "Document Date",
          orderby: 0
      }];
      setCriteriaValues(defaultCriteria);
      originalCriteriaRef.current = []; // No original reference for new items
      setHasUnsavedChanges(false);
      setNextId(1);
    } else {
      // Nothing selected and not adding new category
      setCriteriaValues([]);
      setCategoryValues(categoryFormInitVal);
      setHasUnsavedChanges(false);
      setNextId(1);
    }
  }, [categoryData?.categoryID, categoryData?.name, categoryData?.expiration, categoryData?.criteriaList, setHasUnsavedChanges, addNewCategory]);

  // const isFormActive = addNewCategory && !isCenterPaneDisabled;
  const isDisabled = isCenterPaneDisabled || isTenantBlocked;

  return (
    <FormContainer id="tour-category-criteria-pane" disabled={isCenterPaneDisabled}>
      <InnerContainer disabled={isCenterPaneDisabled} isActive={addNewCategory}>
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            padding: { xs: "2px 16px", sm: "2px 40px" },
            borderRadius: 10,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              margin: "10px 0",
              gap: 1,
            }}
          >
            <StyledTypography style={{ margin: 0 }}>
              Criteria
            </StyledTypography>

            <Button
              id="tour-add-criteria-btn"
              variant="contained"
              startIcon={<AddOutlinedIcon />}
              onClick={addCriteria}
              disabled={isDisabled || !addNewCategory}
              sx={{
                whiteSpace: "nowrap",
                minWidth: "auto",
                marginTop: "12px",
                marginBottom: "10px",
              }}
            >
              {isMobile ? "Add" : "Add New Criteria"}
            </Button>
          </Box>
          
          {hasUnsavedChanges && (
            <span style={{ color: "red", fontSize: 12 }}>
              (Unsaved changes)
            </span>
          )}

          {criteriaValues?.length > 0 ? (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={criteriaValues.map((item) => item.id)}
                strategy={verticalListSortingStrategy}
                disabled={isDisabled || !addNewCategory}
              >
                {criteriaValues.map((item, index) => (
                  <SortableCriteriaItem
                    key={item.id}
                    item={item}
                    index={index}
                    onValueChange={handleCriteriaValueChange}
                    onTypeChange={handleCriteriaTypeChange}
                    onRemove={removeCriteria}
                    disabled={isDisabled || !addNewCategory}
                  />
                ))}
              </SortableContext>
            </DndContext>
          ) : (
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 20, textAlign: "center" }}>
              {!addNewCategory ? "Select a category to view criteria" : "No criteria available for this category"}
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            paddingRight: { xs: "16px", sm: "40px" },
            justifyContent: "flex-end",
            gap: 2,
            marginBottom: "50px",
          }}
        >
          <Button
            variant="contained"
            style={{ marginRight: 20 }}
            onClick={sumbitCompanyForm}
            disabled={
              isDisabled ||
              !addNewCategory ||
              (categoryData?.categoryID && criteriaValues?.length === 0) ||
              createCategory.isPending ||
              updateCategory.isPending ||
              updateCriteriaByCategory.isPending
            }
          >
            {(createCategory.isPending || updateCategory.isPending || updateCriteriaByCategory.isPending) ? (
              <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
            ) : null}
            {categoryData?.categoryID && categoryData?.criteriaList?.length > 0
              ? "Update"
              : "Save"}
          </Button>

          <Button
            variant="outlined"
            onClick={handleCancel}
            disabled={isCenterPaneDisabled || !addNewCategory || createCategory.isPending || updateCategory.isPending || updateCriteriaByCategory.isPending}
          >
            Cancel
          </Button>
        </Box>
      </InnerContainer>
    </FormContainer>
  );
};